import type { ReactElement } from "react";
import { HUD } from "odyssey-shared";
import { logical } from "../../ui/kit/index.ts";
import { bagButtonRect } from "../hud/touch-slots.ts";
import type { HudArea } from "../hud/slots.ts";
import { TOUCH_ICONS, TOUCH_ICON_SIZE } from "../touch/icons.ts";
import { say } from "../../words.ts";

/**
 * The Chat button of a touch page (ADR-061's amendment of 2026-10-31, decision 9: every panel starts closed behind a
 * button of its own): the drawn chat icon with no face, in the top row a gutter left of the Bag button and centred on
 * it, with a dot on its corner while any scope has a line the player has not seen.
 */

/**
 * The dot's side in logical pixels.
 */
const DOT = 8;

/**
 * Where the button stands: its square from the area's top left.
 *
 * @param area The area, with `touch` set.
 * @returns The button's left and top.
 */
export function chatButtonSpot(area: HudArea): { readonly left: number; readonly top: number } {
  const bag = bagButtonRect(area);
  return { left: bag.left - HUD.gutter - TOUCH_ICON_SIZE, top: bag.top + Math.floor((bag.height - TOUCH_ICON_SIZE) / 2) };
}

/**
 * The button.
 *
 * @param props The `area` it stands in, whether a scope has `unread` lines, and what a tap does (`onOpen`).
 * @returns The button.
 */
export function ChatPhoneButton({ area, unread, onOpen }: { readonly area: HudArea; readonly unread: boolean; readonly onOpen: () => void }): ReactElement {
  const spot = chatButtonSpot(area);
  return (
    <button type="button" className="ot-chat__phone-button" data-chat="phone-button" data-chat-unread={unread ? "true" : undefined} aria-label={say(unread ? "chat.openChatUnread" : "chat.openChat")} style={{ left: logical(spot.left), top: logical(spot.top), width: logical(TOUCH_ICON_SIZE), height: logical(TOUCH_ICON_SIZE) }} onClick={onOpen}>
      <img className="ot-chat__phone-icon" src={TOUCH_ICONS.chat} alt="" draggable={false} />
      {unread ? <span className="ot-chat__phone-dot" data-chat="phone-dot" aria-hidden="true" style={{ width: logical(DOT), height: logical(DOT) }} /> : null}
    </button>
  );
}
